import {
  ButtonGroup,
  Divider,
  Flex,
  FormControl,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { MdReceiptLong } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../../../components/card/Card";
import CustomButton from "../../../components/Common/CustomButton";
import DateFormat from "../../../components/Common/DateFormat";
import IsLoading from "../../../components/Common/IsLoading";
import { apiPath } from "../../../utils/Api";
import {
  selectButtonLoading,
  selectEndDate,
  selectFormData,
  selectLoading,
  selectOrder,
  selectOrderPageNo,
  selectOrderPageSize,
  selectOrderTotalRow,
  selectStartDate,
} from "./redux/selector";
import { actions } from "./redux/slice";
import { DataImportantStatusEnum, IUserOrderData } from "./types";
import { CommonTable } from "../../../components/CommonTable";
import { createColumnHelper } from "@tanstack/react-table";
import DateRangeInputs from "../order/DateRangeInputs";
import { toast } from "sonner";
import { DateTime } from "luxon";
import CustomSelect from "../../../components/Common/CustomInput/CustomSelect";
import { StatusData } from "../retailer/Create";

const Create = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const form = useSelector(selectFormData);
  const loader = useSelector(selectLoading);
  const loading = useSelector(selectButtonLoading);
  const pageNo = useSelector(selectOrderPageNo);
  const pageSize = useSelector(selectOrderPageSize);
  const orders = useSelector(selectOrder);
  const total = useSelector(selectOrderTotalRow);
  const startDate = useSelector(selectStartDate);
  const endDate = useSelector(selectEndDate);

  React.useEffect(() => {
    if (params.id) {
      dispatch(actions.doGetById(params?.id));
    }
    return () => {};
  }, [params.id]);
  React.useEffect(() => {
    if (params.id) {
      dispatch(actions.doGetOrderList(params.id));
    }
    return () => {};
  }, [pageNo, pageSize, params.id]);
  const handleCancel = () => {
    dispatch(actions.clearFrom());
    navigate(-1);
  };

  const FieldRow = ({ label, value }: { label: string; value: string }) => (
    <Flex>
      <Text fontWeight="semibold" minW="150px">
        {label}:
      </Text>
      <Text fontWeight="light">{value || "-"}</Text>
    </Flex>
  );
  const handleDownload = (id: string) => {
    window.open(`${apiPath()}user/order/invoice/${id}`);
  };

  const columnHelper = createColumnHelper<IUserOrderData>();
  const columns = [
    columnHelper.accessor("_id", {
      id: "_id",
      header: `${t("common.list.sNo")}`,
      cell: (info) => {
        return (
          <>
            {pageNo === 1 ? (
              <>{info.row.index + 1}</>
            ) : (
              <>{`${(pageNo - 1) * pageSize + info.row.index + 1}`}</>
            )}
          </>
        );
      },
    }),
    columnHelper.accessor("orderId", {
      id: "orderId",
      header: `${t("common.list.orderId")}`,
      cell: (info) => <Text>{info.getValue() || "NA"}</Text>,
    }),
    columnHelper.accessor("totalPrice", {
      id: "totalPrice",
      header: `${t("common.list.price")}`,
      cell: (info) => <Text>¥{info.getValue() || "NA"}</Text>,
    }),

    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: `${t("common.list.createdAt")}`,
      cell: (info) => <Text>{DateFormat(info.getValue()) || "NA"}</Text>,
       sortDescFirst: true,
    }),
    columnHelper.accessor("_id", {
      id: "_id",
      header: `${t("common.list.actions")}`,
      cell: (info) => {
        return (
          <HStack>
            <Tooltip label={t("common.list.invoice")}>
              <IconButton
                aria-label="Export CSV"
                icon={<MdReceiptLong size={20} />}
                onClick={() => {
                  handleDownload(info.row.original.invoiceId);
                }}
                colorScheme="brand"
                variant="solid"
                size="md"
              />
            </Tooltip>
          </HStack>
        );
      },
    }),
  ];

  const handleDate = () => {
    if (startDate === null) {
      toast.error(t("order.list.enterStartDate"));
      return;
    }
    if (endDate === null) {
      toast.error(t("order.list.enterEndDate"));
      return;
    }
    const start =
      typeof startDate === "string" ? DateTime.fromISO(startDate) : startDate;
    const end =
      typeof endDate === "string" ? DateTime.fromISO(endDate) : endDate;

    if (end < start) {
      toast.error(t("order.list.validEndDate"));
      return;
    }
    dispatch(actions.doGetOrderList(params.id || ""));
  };
  const onClearDate = () => {
    dispatch(actions.setEndDate(null));
    dispatch(actions.setStartDate(null));
    dispatch(actions.doGetOrderList(params.id || ""));
  };
  const handleFieldChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target;
    dispatch(actions.updateFormValue({ key: name, value: value }));
  };
    const handleSubmit = () => {
      if (form?._id) {
        dispatch(
          actions.doUpdate({
            callback() {
              dispatch(actions.clearFrom());
              navigate(-1);
            },
          })
        );
      }
    };
  const handleEndDate = (date: DateTime | null) => {
    //@ts-ignore
    dispatch(actions.setEndDate(date?.toFormat("yyyy-MM-dd")));
  };
  const handleStartDate = (date: DateTime | null) => {
    //@ts-ignore
    dispatch(actions.setStartDate(date?.toFormat("yyyy-MM-dd")));
  };
  return (
    <Flex pt={{ base: "130px", md: "80px", xl: "80px" }}>
      <Card>
        {loader ? (
          <IsLoading />
        ) : (
          <FormControl>
            <Stack spacing={4}>
              <FieldRow
                label={t("register.fullName")}
                value={`${form?.lastName} ${form?.firstName}`}
              />
              <FieldRow
                label={t("register.japaneseName")}
                value={`${form?.japaneseLastName} ${form?.japaneseFirstName}`}
              />
              <FieldRow label={t("register.email")} value={form?.email} />
              <FieldRow
                label={t("register.phone")}
                value={form?.mobileNumber}
              />

              <Divider my={2} />
              <Heading size="sm">{t("register.address")}</Heading>
              <FieldRow
                label={t("register.streetAddress")}
                value={form?.streetAddress}
              />
              <FieldRow
                label={t("register.postalCode")}
                value={form?.postCode}
              />
              <FieldRow
                label={t("register.prefecture")}
                value={form?.prefectures}
              />
              <FieldRow
                label={`${t("common.form.importantStatus")}`}
                value={form?.importantStatus}
              />
               <FormControl>
                <SimpleGrid columns={[1, 1, 2, 3]} spacingX={5}>
                
                  <CustomSelect
                    name={"status"}
                    value={form?.status}
                    handleChange={handleFieldChange}
                    title={`${t("common.form.status")}`}
                    data={StatusData}
                  />
                  <CustomSelect
                    name={"importantStatus"}
                    value={form?.importantStatus}
                    handleChange={handleFieldChange}
                    title={`${t("common.form.importantStatus")}`}
                    data={ImportantStatusData}
                  />
                </SimpleGrid>
              </FormControl>
            </Stack>

            <SimpleGrid columns={1} spacing={10}>
              <Flex flexDirection="column">
                <ButtonGroup justifyContent={"flex-end"} mt={8}>
                  <CustomButton
                    handleClick={handleCancel}
                    title={t("common.button.cancel")}
                    variant="light"
                    sx={{ marginRight: "20px" }}
                  />
                  <CustomButton
                    handleClick={handleSubmit}
                    title={t("common.button.save")}
                    loading={loading}
                    sx={{ marginLeft: "20px" }}
                  />
                </ButtonGroup>
              </Flex>
            </SimpleGrid>
            <Stack>
              <CommonTable<IUserOrderData>
                columns={columns}
                data={orders}
                totalCount={total}
                pageNo={pageNo}
                pageSize={pageSize}
                search={""}
                loading={loading}
                onSearch={(v) => dispatch(actions.setSearch(v))}
                onPageChange={(p) => dispatch(actions.setOrderPageNo(p))}
                onPageSizeChange={(s) => dispatch(actions.setOrderPageSize(s))}
                onSortChange={(id, desc) => {
                  dispatch(actions.setOrderBy(id));
                  dispatch(actions.setOrderDir(desc ? "desc" : "asc"));
                }}
                hideAddButton
                showSearch
                filters={
                  <DateRangeInputs
                    startDate={startDate ?? null}
                    endDate={endDate ?? null}
                    handleStartDate={handleStartDate ?? (() => {})}
                    handleEndDate={handleEndDate ?? (() => {})}
                    onSearch={handleDate ?? (() => {})}
                    onClear={onClearDate ?? (() => {})}
                  />
                }
              />
            </Stack>
          </FormControl>
        )}
      </Card>
    </Flex>
  );
};

export default Create;

export const ImportantStatusData = [
  { _id: DataImportantStatusEnum.IMPORTANT, value: "common.text.important" },
  {
    _id: DataImportantStatusEnum.NOT_IMPORTANT,
    value: "common.text.notImportant",
  },
];
